import {
  Component,
  forwardRef,
  Input,
  OnChanges,
  SimpleChanges,
  inject
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR
} from '@angular/forms';
import {
  Address,
  AgpFormUtil,
  CvaFormGroupComponent,
  ResetOptions
} from '@liberty-financial/agp-shared';
import {
  dateBeforeValidator,
  dateGreaterThanValidator,
  dateValidator,
  GuidUtil
} from '@liberty-financial/ng-core';

import { ContactsOverseasAddressOptionsService } from '@features/contacts/address/overseas-address-options';
import { Residence } from '@features/contacts/models';
import { StandardSchemaUtil } from '@shared/utilities/standard-schema-util';

@Component({
  selector: 'spark-contacts-individual-residences',
  templateUrl: './residences.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => IndividualResidencesComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => IndividualResidencesComponent),
      multi: true
    }
  ]
})
export class IndividualResidencesComponent
  extends CvaFormGroupComponent<Residence.Model[]>
  implements OnChanges
{
  @Input() schema!: Residence.SchemaPropertyDefinition;
  @Input() isRequired = false;

  deleted: { [index: number]: boolean } = {};
  expandedIndex = -1;
  readonly addressOptions$ = inject(
    ContactsOverseasAddressOptionsService
  ).options$;
  form = this.fb.array<
    FormGroup<{
      id: FormControl<string>;
      new: FormControl<boolean>;
      deleted: FormControl<boolean>;
      address: FormControl<Address | null>;
      addressType: FormControl<string | null>;
      residentialStatus: FormControl<string | null>;
      startDate: FormControl<string | null>;
      endDate: FormControl<string | null>;
    }>
  >([]);
  formGroup = this.fb.group({
    residences: this.form
  });

  constructor(private readonly fb: FormBuilder) {
    super();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.isRequired) {
      AgpFormUtil.Validators.updateRequiredIfNecessary(this.form, changes.isRequired.currentValue);
    }
  }

  protected reset(opts?: ResetOptions | undefined): void {
    this.form?.reset(undefined, opts);
  }

  protected transformToForm = (v: Residence.Model[]): Residence.Model[] => {
    v.forEach(() => {
      this.add(false);
    });
    return v;
  };

  add(newItem: boolean = true): void {
    const itemForm = this.fb.group({
      id: this.fb.nonNullable.control(GuidUtil.generate()),
      new: this.fb.nonNullable.control(newItem),
      deleted: this.fb.nonNullable.control(false),
      address: [null as Address | null],
      addressType: [null as string | null],
      residentialStatus: [null as string | null],
      startDate: [
        null as string | null,
        [dateValidator(), dateBeforeValidator()]
      ],
      endDate: [
        null as string | null,
        [
          dateValidator(),
          dateBeforeValidator(),
          dateGreaterThanValidator('startDate')
        ]
      ]
    });
    StandardSchemaUtil.attachFormGroupToSchema(itemForm, this.schema);
    this.form.push(itemForm);
    this.expandedIndex = newItem ? this.form.length - 1 : this.expandedIndex;
  }

  remove(i: number): void {
    this.deleted[i] = true;
    this.form.at(i).controls.deleted.setValue(true);
  }
}
